import {
  Asset,
  AssetSummary,
  AssetType,
  DownloadItem,
  FootageCrateEffect,
  GetResolutionLabelParams,
  GraphicsCrateImage,
  RenderCrateEnvironment,
  RenderCrateMaterial,
  RenderCrateModel,
  Resolution,
  ResolutionId,
  ResolutionLabel,
  Resolutions,
  SoundsCrateSound,
} from '@/lib/assets/types'

export const isFootageCrateEffect = (asset: { __typename: AssetType }): asset is FootageCrateEffect => {
  return asset.__typename === 'fc_effects'
}

export const isGraphicsCrateImage = (asset: { __typename: AssetType }): asset is GraphicsCrateImage => {
  return asset.__typename === 'gc_images'
}

export const isProductionCrateMisc = (asset: { __typename: AssetType }): asset is GraphicsCrateImage => {
  return asset.__typename === 'pc_misc'
}

export const isProductionCrateTutorial = (asset: { __typename: AssetType }): asset is GraphicsCrateImage => {
  return asset.__typename === 'pc_tutorials'
}

export const isRenderCrateEnvironment = (asset: { __typename: AssetType }): asset is RenderCrateEnvironment => {
  return asset.__typename === 'rc_environments'
}

export const isRenderCrateMaterial = (asset: { __typename: AssetType }): asset is RenderCrateMaterial => {
  return asset.__typename === 'rc_materials'
}

export const isRenderCrateModel = (asset: { __typename: AssetType }): asset is RenderCrateModel => {
  return asset.__typename === 'rc_objects'
}

export const isSoundsCrateSound = (asset: { __typename: AssetType }): asset is SoundsCrateSound => {
  return asset.__typename === 'sc_sounds'
}

/**
 * Gets a resolution label by its dimensions.
 */
export function getResolutionLabelByDimensions(
  dimensions: string,
  { appendDimensions, originalDimensions }: GetResolutionLabelParams = {
    appendDimensions: false,
  }
): ResolutionLabel {
  const resolution = getResolutionByDimensions(dimensions)

  if (!resolution) {
    return dimensions
  }

  if (appendDimensions) {
    // If resolution dimensions are not in the format of width x height,
    // we need to get the lower dimensions from the original dimensions
    let resolvedDimensions = ''

    // When dimensions and name don't match, it means the resolution is in the format of width x height
    if (resolution.dimensions !== resolution.name) {
      resolvedDimensions = resolution.dimensions
    } else {
      const lowerResolutions =
        appendDimensions && originalDimensions ? getLowerResolutions(originalDimensions) : undefined

      if (lowerResolutions) {
        const resolutionDimensions = lowerResolutions[resolution.id]

        if (resolutionDimensions) {
          resolvedDimensions = `${resolutionDimensions.width}x${resolutionDimensions.height}`
        }
      }
    }

    resolution.name += resolvedDimensions ? ` (${resolvedDimensions})` : ''
  }

  return resolution.name
}

/**
 * Standard resolution values
 */
export const resolutionsMap: Record<keyof Resolutions, { width: number; height: number }> = {
  '8k': { width: 7680, height: 4320 },
  '7k': { width: 7168, height: 3072 },
  '6k': { width: 6144, height: 3072 },
  '5k': { width: 5120, height: 2880 },
  '4k': { width: 3840, height: 2160 },
  '3k': { width: 3072, height: 1728 },
  '2k': { width: 2048, height: 1080 },
  '1k': { width: 1024, height: 540 },
  hd: { width: 1920, height: 1080 },
  sd: { width: 720, height: 480 },
  lq: { width: 320, height: 240 },
}

/**
 * Gets a resolution by its dimensions in the format of width x height.
 * If the resolution is not in the format of width x height, it will return the resolution name.
 */
export function getResolutionByDimensions(dimensions: string): Resolution {
  // Split dimensions and cast to number
  const [width, height] = dimensions.split('x').map(Number)

  // Find the resolution
  const findResolution = (dimensions: string) => {
    // Resolution is not in the format of width x height
    if (isNaN(width) || isNaN(height)) {
      return {
        id: dimensions.toLowerCase(),
        slug: dimensions.toLowerCase(),
        name: dimensions,
        dimensions,
      }
    }

    // 8K
    if (width >= 7680 /* && height >= 4320*/) {
      return {
        id: '8k',
        slug: '8k',
        name: '8K',
        dimensions,
      }
    }

    // 7K
    if (width >= 7168 /* && height >= 3072*/) {
      return {
        id: '7k',
        slug: '7k',
        name: '7K',
        dimensions,
      }
    }

    // 6K
    if (width >= 6144 /* && height >= 3072*/) {
      return {
        id: '6k',
        slug: '6k',
        name: '6K',
        dimensions,
      }
    }

    // 5K
    if (width >= 5120 /* && height >= 2880*/) {
      return {
        id: '5k',
        slug: '5k',
        name: '5K',
        dimensions,
      }
    }

    // 4k
    if (width >= 3840 /* && height >= 2160*/) {
      return {
        id: '4k',
        slug: '4k',
        name: '4K',
        dimensions,
      }
    }

    // 3k
    if (width >= 3072 /* && height >= 1728*/) {
      return {
        id: '3k',
        slug: '3k',
        name: '3K',
        dimensions,
      }
    }

    // 2K
    if (width >= 2048 /* && height >= 1080*/) {
      return {
        id: '2k',
        slug: '2k',
        name: '2K',
        dimensions,
      }
    }

    // HD
    if (width >= 1920 /* && height >= 1080*/) {
      return {
        id: 'hd',
        slug: 'hd',
        name: 'HD',
        dimensions,
      }
    }

    // 1K
    if (width >= 1024 /* && height >= 540*/) {
      return {
        id: '1k',
        slug: '1k',
        name: '1K',
        dimensions,
      }
    }

    // SD
    if (width >= 720 /* && height >= 480*/) {
      return {
        id: 'sd',
        slug: 'sd',
        name: 'SD',
        dimensions,
      }
    }

    // Default
    return {
      id: dimensions,
      slug: dimensions,
      name: dimensions,
      dimensions,
    }
  }

  return findResolution(dimensions)
}

export function getFormatLabel(format: string): string {
  if (format === 'H.264') {
    return 'MP4'
  }

  return format
}

/**
 * Given an asset resolution in the format of width x height, returns lower resolutions.
 * @param resolution
 */
export function getLowerResolutions(resolution: string): Record<ResolutionId, { width: number; height: number }> {
  const [originalWith, originalHeight] = resolution.split('x').map(Number)

  const lowerResolutions: Record<ResolutionId, { width: number; height: number }> = {}

  // Iterate resolutionsMap
  for (const [key, value] of Object.entries(resolutionsMap)) {
    const { width, height } = value

    // Skip if the evaluated resolution is higher than the original resolution
    if (width > originalWith || height > originalHeight) {
      continue
    }

    const isFirstMatchedResolution = Object.keys(lowerResolutions).length === 0

    if (isFirstMatchedResolution) {
      lowerResolutions[key as ResolutionId] = {
        width: originalWith,
        height: originalHeight,
      }
      continue
    }

    // Keep the size within the largest dimension
    const largestOriginalDimension = Math.max(originalWith, originalHeight)

    const widthRatio = width / largestOriginalDimension
    const heightRatio = height / largestOriginalDimension

    const maxRatio = Math.max(widthRatio, heightRatio)

    lowerResolutions[key as ResolutionId] = {
      width: Math.round(originalWith * maxRatio),
      height: Math.round(originalHeight * maxRatio),
    }
  }

  return lowerResolutions
}

/**
 * Get the key of an asset.
 * Used as asset slug, and also for asset cache key and favorite key.
 * @param asset
 */
export function getAssetKey(asset: Asset | AssetSummary): string {
  let assetKey: string | undefined = undefined

  if ('file_name' in asset) {
    assetKey = asset.file_name
  }

  if (!assetKey && 'location' in asset) {
    assetKey = asset.location
  }

  return assetKey ?? ''
}

/**
 * Get the index of the resolution factor in the asset's variant_factors array
 */
export function getResolutionIndex(asset: Asset | AssetSummary): number {
  if (!asset || !('dl_opts' in asset) || !asset.dl_opts) {
    return -1
  }

  return asset.dl_opts.variant_factors.findIndex((factor) => factor.type === 'res')
}

/**
 * Get the index of the format factor in the asset's variant_factors array
 */
export function getFormatIndex(asset: Asset | AssetSummary): number {
  if (!asset || !('dl_opts' in asset) || !asset.dl_opts) {
    return -1
  }

  return asset.dl_opts.variant_factors.findIndex((factor) => factor.type === 'format')
}

/**
 * Find a download item of an asset given the resolution and format.
 * @param asset
 * @param resolution
 * @param format
 */
export function getDownloadItem({
  asset,
  resolution,
  format,
}: {
  asset: Asset | AssetSummary
  format?: string
  resolution?: string
}): DownloadItem | null {
  const resolutionIndex = getResolutionIndex(asset)
  const formatIndex = getFormatIndex(asset)

  return 'dl_opts' in asset
    ? (asset.dl_opts.items.find(
        (item) => item.variant_labels[resolutionIndex] === resolution && item.variant_labels[formatIndex] === format
      ) ?? null)
    : null
}

/**
 * Find the download item that matches the selected resolution and format.
 * @param asset
 * @param resolution
 * @param format
 * @param userToken
 */
export function getDownloadUrl({
  asset,
  resolution,
  format,
  userToken,
}: {
  asset: Asset | AssetSummary
  format?: string
  resolution?: string
  userToken?: string
}): string | null {
  const downloadItem = getDownloadItem({ asset, resolution, format })

  if (!downloadItem) {
    return null
  }

  const params = downloadItem.dl_url.map((downloadUrl) => `${downloadUrl.param}=${downloadUrl.value}`)

  // Extra params for RenderCrate
  if (asset.__typename === 'rc_objects') {
    params.push(`type=obj`)
  }

  if (asset.__typename === 'rc_materials') {
    params.push(`type=material`)
  }

  if (asset.__typename === 'rc_environments') {
    params.push(`type=environment`)
  }

  const assetKey = getAssetKey(asset)
  const query = params.join('&')

  return (
    `https://member.productioncrate.com/download.php?title=${assetKey}${query ? '&' + query : ''}&redirect=1` +
    (userToken ? `&request_token=${userToken}` : '')
  )
}

export function getDetailUrl(asset: Asset | AssetSummary): string | null {
  const assetKey = getAssetKey(asset)

  if (isFootageCrateEffect(asset)) {
    return `https://vfx.productioncrate.com/video-effects/${assetKey}`
  }

  if (isGraphicsCrateImage(asset)) {
    return `https://graphics.productioncrate.com/stock-image-hd/${assetKey}`
  }

  if (isRenderCrateModel(asset)) {
    return `https://www.productioncrate.com/objects/${assetKey}`
  }

  if (isRenderCrateMaterial(asset)) {
    return `https://www.productioncrate.com/materials/${assetKey}`
  }

  if (isRenderCrateEnvironment(asset)) {
    return `https://www.productioncrate.com/environments/${assetKey}`
  }

  if (isSoundsCrateSound(asset)) {
    return `https://sfx.productioncrate.com/sound-effects/${assetKey}`
  }

  console.warn('Asset detail URL not found for', assetKey)

  return null
}

/**
 * Get asset image preview if applies.
 */
export function getImagePreview(asset: Asset | AssetSummary): string | null {
  if (isFootageCrateEffect(asset)) {
    return `https://p.productioncrate.com/stock-hd/effects/${asset.location}@2X.png`
  }

  if (isGraphicsCrateImage(asset)) {
    // Note that Asset and AssetSummary have different paths
    return `https://p.productioncrate.com/stock-hd/graphics/${
      asset.preview.endsWith('.png') ? asset.preview : `${asset.preview}/${asset.file_name}_prev_lg.png`
    }`
  }

  if (isRenderCrateModel(asset)) {
    return `https://p.productioncrate.com/stock-hd/objects/${asset.file_name}/front.jpg`
  }

  if (isRenderCrateMaterial(asset)) {
    return `https://p.productioncrate.com/stock-hd/materials/${asset.file_name}/1-xs.png`
  }

  if (isRenderCrateEnvironment(asset)) {
    return `https://p.productioncrate.com/stock-hd/environments/${asset.file_name}_prev_xs.jpg`
  }

  return null
}

/**
 * Get asset video preview if applies.
 */
export function getVideoPreview(asset: Asset | AssetSummary): string | null {
  if (isFootageCrateEffect(asset)) {
    return `https://p.productioncrate.com/stock-hd/effects/${asset.location}-s.mp4`
  }

  return null
}

/**
 * Get asset 360 preview if applies.
 */
export function get360Preview(asset: Asset | AssetSummary): string | null {
  if (isRenderCrateModel(asset)) {
    return `https://p.productioncrate.com/stock-hd/objects/${asset.file_name}/sprite.jpg`
  }

  return null
}

/**
 * Get asset panorama preview if applies.
 */
export function getPanoramaPreview(asset: Asset | AssetSummary): string | null {
  if (isRenderCrateEnvironment(asset)) {
    return `https://p.productioncrate.com/stock-hd/environments/${asset.file_name}_prev_sm.jpg`
  }

  return null
}

/**
 * Get asset audio preview if applies.
 */
export function getAudioPreview(asset: Asset | AssetSummary): string | null {
  if (isSoundsCrateSound(asset)) {
    return getAssetKey(asset)
  }

  return null
}
